import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Loader2, Users } from 'lucide-react';

import { Access, Role } from 'src/shared/types';
import {
    useUserManagementProjectListQuery,
    useLazyUserManagementProjectListQuery,
    useUserCountSummaryQuery,
} from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import {
    useGetUserManagementMatrixRolesQuery,
    useBulkApplyUserManagementSiteRoleMutation,
} from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import type { BasicProjectListItem, CustomerProject } from 'src/app/projects/views/project-list/state/api/queries/projectsListQuery';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useDebouncedListSearch } from 'src/shared/hooks/useDebouncedListSearch';
import Checkbox from 'src/shared/components/checkbox/Checkbox';
import Input from 'src/shared/components/input/Input';
import { getRoleLabel } from 'src/app/admin/views/user-management/components/userManagementHelpers';
import Pagination from 'src/shared/components/pagination/Pagination';
import Select from 'src/shared/components/select/Select';
import { SecondaryButton } from 'src/shared/components/button/Button';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import { diffUserProjectRoles } from 'src/app/admin/views/user-management/userProjectAccessMutations';
import ColumnSelectAllCheckbox from './ColumnSelectAllCheckbox';
import {
    aggregateDisableSubmit,
    applyBulkSiteRoleResultToProjects,
    bulkTurnOffRoleForEligibleRows,
    bulkTurnOnRoleForEligibleRows,
    buildMatrixRowsFromProjectList,
    buildProjectsFromRows,
    collectMatrixRoleNamesFromProjects,
    mergeMatrixRoleColumnNames,
    isCellCheckboxDisabled,
    isRoleUnavailableForProject,
    matrixCellHighlightClass,
    mergeProjectsAfterMatrixPageSync,
    MATRIX_BULK_ACTION_MIN_TOTAL,
    MATRIX_SITES_PAGE_SIZE,
    filterVisibleMatrixRoleColumns,
    roleVisibleInMatrix,
    toggleMatrixRowRoleChecked,
    userCountSummaryToRoleStatsByProject,
    type MatrixRowState,
} from './projectPermissionsMatrixHelpers';
import styles from './ProjectPermissionsMatrix.module.scss';
import type { AssignProjectProps } from '../../AssignProjects';

/** Normalises list API rows to `CustomerProject` for shared matrix row builder */
function projectListPageToCustomerProjects(projects: BasicProjectListItem[]): CustomerProject[] {
    return projects.map(
        p =>
            ({
                projectId: p.projectId,
                projectName: p.projectName,
                projectCode: p.projectCode,
                samplesCount: p.samplesCount,
                subscription: p.subscription,
                features: p.features,
                status: p.status,
                projectType: '',
                samplingCadence: { type: '' },
            } as unknown as CustomerProject)
    );
}

const BULK_ACTION_ASSIGN = 'assign';
const BULK_ACTION_UNASSIGN = 'unassign';
type BulkActionKind = typeof BULK_ACTION_ASSIGN | typeof BULK_ACTION_UNASSIGN;

export type ProjectPermissionsMatrixEditableProps = AssignProjectProps & {
    /** Resets bulk UI and pagination when the edited/created user context changes */
    matrixSessionKey: string;
    /** When set with immediate persist, bulk uses the server mutation; also passed to the matrix roles query. */
    matrixTargetUserId?: number;
};

/**
 * Editable site × role matrix for add/edit user flows. Loads one page of sites via `userManagementProjectList`
 * (manageable sites, name order), merges each page into `formState.projects`, and optionally
 * calls `persistUserProjectAccess` after cell/column/bulk changes for immediate API sync (edit user modal).
 * Bulk assign/unassign to all filtered sites is only available when editing (`Access.UPDATE`), not when creating a user.
 */
const ProjectPermissionsMatrixEditable = ({
    formState,
    onFormChange,
    disabled = false,
    formMode = Access.UPDATE,
    matrixSessionKey,
    matrixTargetUserId,
    persistUserProjectAccess,
    isSavingChanges = false,
    onSitePermissionsBulkSynced,
    showBulkSiteRoleActions = true,
}: ProjectPermissionsMatrixEditableProps) => {
    const { projects, is_internal_user: isInternalUser } = formState;
    const { currentCustomerId, currentCustomerProjectIds } = useCustomer();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { hasProjectLevelRole, hasCustomerLevelRole } = usePermissions();
    const { inputValue, activeSearch, onInputChange } = useDebouncedListSearch();
    const [apiPage, setApiPage] = useState(1);
    const [bulkRoleSelection, setBulkRoleSelection] = useState('');
    const [bulkActionKind, setBulkActionKind] = useState<BulkActionKind>(BULK_ACTION_ASSIGN);
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [bulkApplying, setBulkApplying] = useState(false);
    const [bulkResultMessage, setBulkResultMessage] = useState<string | null>(null);
    const [triggerProjectListAll] = useLazyUserManagementProjectListQuery();
    const [triggerBulkApplySiteRole] = useBulkApplyUserManagementSiteRoleMutation();
    /** Omit `searchText`: column catalogue must reflect all manageable sites’ subscription roles, not only the current filter (empty search would otherwise yield only the target user’s roles). */
    const { currentData: matrixRolesFromApi, isFetching: matrixRolesFetching } = useGetUserManagementMatrixRolesQuery(
        {
            customerId: currentCustomerId || '',
            includeRolesFromUserId: matrixTargetUserId,
        },
        { skip: !currentCustomerId }
    );
    /** Prevents overlapping bulk apply while a run is in flight */
    const bulkApplyLock = useRef(false);

    const {
        data: projectListStale,
        currentData: projectListCurrent,
        isFetching: isProjectListFetching,
    } = useUserManagementProjectListQuery(
        {
            customerId: currentCustomerId || '',
            pagination: { page: apiPage, pageSize: MATRIX_SITES_PAGE_SIZE },
            searchText: activeSearch,
        },
        { skip: !currentCustomerId, refetchOnMountOrArgChange: true }
    );

    /** `data` retains last result while new args fetch; `currentData` is only for fulfilled current args */
    const projectListData = projectListCurrent ?? projectListStale;

    const totalManageable = useMemo(
        () => projectListCurrent?.pagination?.total ?? projectListStale?.pagination?.total ?? 0,
        [projectListCurrent?.pagination?.total, projectListStale?.pagination?.total]
    );

    const projectsData = useMemo((): CustomerProject[] | null => {
        if (!projectListData) {
            return null;
        }
        return projectListPageToCustomerProjects(projectListData.projects);
    }, [projectListData]);

    const projectIdsForUserCount = useMemo(() => {
        if (!projectsData?.length) {
            return [] as string[];
        }
        return projectsData.map(p => p.projectId).filter(Boolean);
    }, [projectsData]);

    const { currentData: userCountSummaries, isFetching: userCountsFetching } = useUserCountSummaryQuery(
        { projectIds: projectIdsForUserCount, includeDisabledUser: false },
        { skip: !currentCustomerId || projectIdsForUserCount.length === 0 }
    );

    const roleStatsByProject = useMemo(() => userCountSummaryToRoleStatsByProject(userCountSummaries), [userCountSummaries]);

    /** Server already filtered to manageable sites; every row on this page is assignable */
    const assignableIds = useMemo(() => {
        if (!projectsData) {
            return new Set<string>();
        }
        return new Set(projectsData.map(p => p.projectId));
    }, [projectsData]);

    const prevSessionKeyRef = useRef(matrixSessionKey);

    const [rows, setRows] = useState<MatrixRowState[]>([]);
    /** Serialises cell/column persist so rapid clicks don’t interleave */
    const cellPersistLock = useRef(false);
    const onFormChangeRef = useRef(onFormChange);
    onFormChangeRef.current = onFormChange;
    const projectsRef = useRef(projects);
    projectsRef.current = projects;
    /** Skips redundant `onFormChange` when rebuild would produce identical projects + disableSubmit */
    const lastSyncedSig = useRef('');

    useEffect(() => {
        if (prevSessionKeyRef.current !== matrixSessionKey) {
            prevSessionKeyRef.current = matrixSessionKey;
            lastSyncedSig.current = '';
        }
    }, [matrixSessionKey]);

    const matrixRoleColumnNames = useMemo(() => {
        const merged = mergeMatrixRoleColumnNames(matrixRolesFromApi ?? [], projects);
        if (merged.length > 0) {
            return merged;
        }
        return collectMatrixRoleNamesFromProjects(projects);
    }, [matrixRolesFromApi, projects]);

    /** Rebuild visible page rows from project list + form assignments; merge into full `projects` and push `disableSubmit` */
    useEffect(() => {
        if (!projectsData || projectsData.length === 0) {
            setRows([]);
            return;
        }

        const built = buildMatrixRowsFromProjectList(projectsData, projects, assignableIds, matrixRoleColumnNames, roleStatsByProject);
        setRows(built);

        const nextFromPage = buildProjectsFromRows(built);
        const pageIds = built.map(r => r.projectId);
        const mergedProjects = mergeProjectsAfterMatrixPageSync(projectsRef.current, pageIds, nextFromPage);
        const disableSubmit = aggregateDisableSubmit(built, formMode);
        const sig = JSON.stringify({ mergedProjects, disableSubmit });
        if (lastSyncedSig.current !== sig) {
            lastSyncedSig.current = sig;
            onFormChangeRef.current({ projects: mergedProjects, disableSubmit });
        }
    }, [projectsData, projects, assignableIds, formMode, matrixRoleColumnNames, roleStatsByProject]);

    const isCurrentUserCustomerAdmin = hasCustomerLevelRole({
        customerId: currentCustomerId,
        role: Role.CUSTOMER_ADMIN,
    });

    const columnRoles = matrixRoleColumnNames;

    const visibleRoleColumns = useMemo(
        () =>
            filterVisibleMatrixRoleColumns(columnRoles, {
                isInternalUser,
                isCurrentUserCustomerAdmin,
                isCurrentUserSuperAdmin,
                customerProjectIds: currentCustomerProjectIds,
                hasProjectLevelRole,
            }),
        [columnRoles, isInternalUser, isCurrentUserCustomerAdmin, hasProjectLevelRole, isCurrentUserSuperAdmin, currentCustomerProjectIds]
    );

    useEffect(() => {
        setBulkResultMessage(null);
    }, [activeSearch]);

    useEffect(() => {
        setBulkResultMessage(null);
        setBulkRoleSelection('');
        setBulkActionKind(BULK_ACTION_ASSIGN);
    }, [matrixSessionKey]);

    useEffect(() => {
        setApiPage(1);
    }, [matrixSessionKey, activeSearch]);

    /** Only clamp when list data is settled for current args (not stale `data` from previous page). */
    useEffect(() => {
        if (isProjectListFetching || !projectListCurrent?.pagination) {
            return;
        }
        const total = projectListCurrent.pagination.total;
        const lastPage = Math.max(1, Math.ceil(total / MATRIX_SITES_PAGE_SIZE));
        setApiPage(p => Math.min(p, lastPage));
    }, [isProjectListFetching, projectListCurrent]);

    /** Applies row state locally and mirrors derived `projects` + submit gate to parent */
    const pushFormUpdate = useCallback(
        (nextRows: MatrixRowState[]) => {
            setRows(nextRows);
            const nextFromPage = buildProjectsFromRows(nextRows);
            const pageIds = nextRows.map(r => r.projectId);
            const mergedProjects = mergeProjectsAfterMatrixPageSync(projectsRef.current, pageIds, nextFromPage);
            const disableSubmit = aggregateDisableSubmit(nextRows, formMode);
            lastSyncedSig.current = JSON.stringify({ mergedProjects, disableSubmit });
            onFormChangeRef.current({
                projects: mergedProjects,
                disableSubmit,
            });
        },
        [formMode]
    );

    /** Single cell: optional immediate `persistUserProjectAccess`; rolls back UI on failure */
    const onToggleCell = async (projectId: string, roleName: string, checked: boolean) => {
        const row = rows.find(r => r.projectId === projectId);
        const entry = row?.countByRoles.find(c => c.name === roleName);
        if (!row?.assignable || !entry) {
            return;
        }
        const isCurrentUserProjectAdmin = hasProjectLevelRole({
            projectId,
            role: Role.PROJECT_ADMIN,
        });
        if (
            isCellCheckboxDisabled(entry, {
                disabled,
                formMode,
                isCurrentUserCustomerAdmin,
                isCurrentUserProjectAdmin,
            })
        ) {
            return;
        }

        const nextRows = toggleMatrixRowRoleChecked(rows, projectId, roleName, checked);
        const mergedAfterToggle = mergeProjectsAfterMatrixPageSync(
            projectsRef.current,
            nextRows.map(r => r.projectId),
            buildProjectsFromRows(nextRows)
        );

        if (persistUserProjectAccess) {
            if (cellPersistLock.current) {
                return;
            }
            cellPersistLock.current = true;
            pushFormUpdate(nextRows);
            const roleChanges = checked
                ? { add: [{ projectId, role: roleName }], remove: [] as { projectId: string; role: string }[] }
                : { add: [] as { projectId: string; role: string }[], remove: [{ projectId, role: roleName }] };
            try {
                await persistUserProjectAccess({ roleChanges, syncedProjects: mergedAfterToggle });
            } catch {
                pushFormUpdate(toggleMatrixRowRoleChecked(rows, projectId, roleName, !checked));
            } finally {
                cellPersistLock.current = false;
            }
            return;
        }

        pushFormUpdate(nextRows);
    };

    /** Tri-state from eligible assignable rows only (matches header checkbox semantics) */
    const columnSelectAllState = useCallback(
        (roleName: string): 'checked' | 'unchecked' | 'indeterminate' => {
            let eligible = 0;
            let checked = 0;
            for (const row of rows) {
                if (!row.assignable) {
                    continue;
                }
                const entry = row.countByRoles.find(c => c.name === roleName);
                if (!entry) {
                    continue;
                }
                const isCurrentUserProjectAdmin = hasProjectLevelRole({
                    projectId: row.projectId,
                    role: Role.PROJECT_ADMIN,
                });
                if (
                    !roleVisibleInMatrix(roleName, {
                        isInternalUser,
                        isCurrentUserCustomerAdmin,
                        isCurrentUserProjectAdmin,
                        isActorSuperAdmin: isCurrentUserSuperAdmin,
                    })
                ) {
                    continue;
                }
                if (
                    isCellCheckboxDisabled(entry, {
                        disabled,
                        formMode,
                        isCurrentUserCustomerAdmin,
                        isCurrentUserProjectAdmin,
                    })
                ) {
                    continue;
                }
                eligible += 1;
                if (entry.checked) {
                    checked += 1;
                }
            }
            if (eligible === 0) {
                return 'unchecked';
            }
            if (checked === eligible) {
                return 'checked';
            }
            if (checked === 0) {
                return 'unchecked';
            }
            return 'indeterminate';
        },
        [rows, disabled, formMode, isInternalUser, isCurrentUserCustomerAdmin, hasProjectLevelRole, isCurrentUserSuperAdmin]
    );

    /** Turn all eligible cells in this column on or off; may batch-persist */
    const onColumnHeaderToggle = async (roleName: string) => {
        const state = columnSelectAllState(roleName);
        const turnOn = state !== 'checked';

        const isCurrentUserProjectAdminFor = (pid: string) => hasProjectLevelRole({ projectId: pid, role: Role.PROJECT_ADMIN });

        let next = rows;
        if (turnOn) {
            for (const row of rows) {
                if (!row.assignable) {
                    continue;
                }
                const entry = row.countByRoles.find(c => c.name === roleName);
                if (!entry || entry.checked) {
                    continue;
                }
                const isPA = isCurrentUserProjectAdminFor(row.projectId);
                if (
                    !roleVisibleInMatrix(roleName, {
                        isInternalUser,
                        isCurrentUserCustomerAdmin,
                        isCurrentUserProjectAdmin: isPA,
                        isActorSuperAdmin: isCurrentUserSuperAdmin,
                    })
                ) {
                    continue;
                }
                if (
                    isCellCheckboxDisabled(entry, {
                        disabled,
                        formMode,
                        isCurrentUserCustomerAdmin,
                        isCurrentUserProjectAdmin: isPA,
                    })
                ) {
                    continue;
                }
                next = toggleMatrixRowRoleChecked(next, row.projectId, roleName, true);
            }
        } else {
            for (const row of rows) {
                const entry = row.countByRoles.find(c => c.name === roleName);
                if (!entry || !entry.checked || !row.assignable) {
                    continue;
                }
                const isPA = isCurrentUserProjectAdminFor(row.projectId);
                if (
                    isCellCheckboxDisabled(entry, {
                        disabled,
                        formMode,
                        isCurrentUserCustomerAdmin,
                        isCurrentUserProjectAdmin: isPA,
                    })
                ) {
                    continue;
                }
                next = toggleMatrixRowRoleChecked(next, row.projectId, roleName, false);
            }
        }

        if (next === rows) {
            return;
        }

        if (persistUserProjectAccess) {
            if (cellPersistLock.current) {
                return;
            }
            cellPersistLock.current = true;
            const prevRows = rows;
            const pageIds = prevRows.map(r => r.projectId);
            const mergedBefore = mergeProjectsAfterMatrixPageSync(projectsRef.current, pageIds, buildProjectsFromRows(prevRows));
            const mergedAfter = mergeProjectsAfterMatrixPageSync(
                projectsRef.current,
                next.map(r => r.projectId),
                buildProjectsFromRows(next)
            );
            const roleChanges = diffUserProjectRoles(mergedBefore, mergedAfter, pageIds);
            pushFormUpdate(next);
            try {
                await persistUserProjectAccess({ roleChanges, syncedProjects: mergedAfter });
            } catch {
                pushFormUpdate(prevRows);
            } finally {
                cellPersistLock.current = false;
            }
            return;
        }

        pushFormUpdate(next);
    };

    /** Loads all filtered sites + chunked summaries, applies role on/off, then optional persist */
    const runBulkSiteRoleAction = useCallback(async () => {
        if (!currentCustomerId || !bulkRoleSelection || totalManageable < 1) {
            return;
        }
        if (bulkApplyLock.current || cellPersistLock.current) {
            return;
        }
        bulkApplyLock.current = true;
        setBulkApplying(true);
        setBulkResultMessage(null);
        try {
            /** Server mutation when editing a user with incremental save (`EditProjectUser`). Otherwise client-side simulation (load all filtered sites, apply role matrix rules, then optional `persistUserProjectAccess` diff) — e.g. **Reassign user to organisation** has no `persistUserProjectAccess` and the user may not be on the customer yet, so `bulkApplyUserManagementSiteRole` cannot run. */
            const useServerBulk = typeof matrixTargetUserId === 'number' && persistUserProjectAccess;

            if (useServerBulk) {
                const apiRes = await triggerBulkApplySiteRole({
                    customerId: currentCustomerId,
                    userId: matrixTargetUserId,
                    role: bulkRoleSelection,
                    assign: bulkActionKind === BULK_ACTION_ASSIGN,
                    searchText: activeSearch || undefined,
                }).unwrap();

                const mergedProjects = applyBulkSiteRoleResultToProjects(
                    projectsRef.current,
                    apiRes.changedProjectIds,
                    bulkRoleSelection,
                    bulkActionKind === BULK_ACTION_ASSIGN
                );
                const disableSubmit = formMode === Access.CREATE ? !mergedProjects.some(p => (p.roles?.length ?? 0) > 0) : false;

                const noSitesInFilter =
                    apiRes.changedCount === 0 &&
                    apiRes.skippedAlreadyHadRole === 0 &&
                    apiRes.skippedIneligible === 0 &&
                    apiRes.skippedNotHadRole === 0;

                if (noSitesInFilter) {
                    setBulkResultMessage('No sites to update.');
                    return;
                }

                if (apiRes.changedCount === 0) {
                    setBulkResultMessage(
                        bulkActionKind === BULK_ACTION_UNASSIGN
                            ? 'No changes: no sites had this role selected, or sites were not eligible to change.'
                            : 'No changes: sites already had this role, or were not eligible to change.'
                    );
                    return;
                }

                lastSyncedSig.current = JSON.stringify({ mergedProjects, disableSubmit });
                onFormChangeRef.current({ projects: mergedProjects, disableSubmit });
                onSitePermissionsBulkSynced?.(mergedProjects);

                const roleLabel = getRoleLabel(bulkRoleSelection);
                const parts =
                    bulkActionKind === BULK_ACTION_UNASSIGN
                        ? [`Removed ${roleLabel} from ${apiRes.changedCount} site${apiRes.changedCount === 1 ? '' : 's'}.`]
                        : [`Applied ${roleLabel} to ${apiRes.changedCount} site${apiRes.changedCount === 1 ? '' : 's'}.`];
                if (bulkActionKind === BULK_ACTION_UNASSIGN) {
                    if (apiRes.skippedNotHadRole > 0) {
                        parts.push(`${apiRes.skippedNotHadRole} did not have this role.`);
                    }
                } else {
                    if (apiRes.skippedAlreadyHadRole > 0) {
                        parts.push(`${apiRes.skippedAlreadyHadRole} already had this role.`);
                    }
                    if (apiRes.skippedIneligible > 0) {
                        parts.push(`${apiRes.skippedIneligible} skipped (not eligible or at capacity).`);
                    }
                }
                setBulkResultMessage(parts.join(' '));
                return;
            }

            const listData = await triggerProjectListAll({
                customerId: currentCustomerId,
                pagination: { page: 1, pageSize: Math.max(totalManageable, 1) },
                searchText: activeSearch,
            }).unwrap();

            const allProjects = projectListPageToCustomerProjects(listData.projects);
            const allProjectIds = allProjects.map(p => p.projectId).filter(Boolean);
            if (allProjectIds.length === 0) {
                setBulkResultMessage('No sites to update.');
                return;
            }

            const assignableIds = new Set(allProjectIds);
            /** Client bulk without server mutation uses page-scoped `userCountSummary` only; capacity checks off the current page are best-effort. */
            const bulkRoleStats = roleStatsByProject;
            const builtRows = buildMatrixRowsFromProjectList(
                allProjects,
                projectsRef.current,
                assignableIds,
                matrixRoleColumnNames,
                bulkRoleStats
            );

            const ctx = {
                disabled,
                formMode,
                isInternalUser,
                isCurrentUserCustomerAdmin,
                isProjectAdminFor: (projectId: string) => hasProjectLevelRole({ projectId, role: Role.PROJECT_ADMIN }),
                isActorSuperAdmin: isCurrentUserSuperAdmin,
            };

            let rowsOut: MatrixRowState[];
            let effectCount: number;
            let resultParts: string[];

            if (bulkActionKind === BULK_ACTION_UNASSIGN) {
                const u = bulkTurnOffRoleForEligibleRows(builtRows, bulkRoleSelection, ctx);
                rowsOut = u.rows;
                effectCount = u.removedCount;
                const roleLabel = getRoleLabel(bulkRoleSelection);
                resultParts = [`Removed ${roleLabel} from ${u.removedCount} site${u.removedCount === 1 ? '' : 's'}.`];
                if (u.skippedNotHad > 0) {
                    resultParts.push(`${u.skippedNotHad} did not have this role.`);
                }
            } else {
                const a = bulkTurnOnRoleForEligibleRows(builtRows, bulkRoleSelection, ctx);
                rowsOut = a.rows;
                effectCount = a.appliedCount;
                const roleLabel = getRoleLabel(bulkRoleSelection);
                resultParts = [`Applied ${roleLabel} to ${a.appliedCount} site${a.appliedCount === 1 ? '' : 's'}.`];
                if (a.skippedAlreadyHad > 0) {
                    resultParts.push(`${a.skippedAlreadyHad} already had this role.`);
                }
            }

            const fromAll = buildProjectsFromRows(rowsOut);
            const mergedProjects = mergeProjectsAfterMatrixPageSync(projectsRef.current, allProjectIds, fromAll);
            const disableSubmit = aggregateDisableSubmit(rowsOut, formMode);

            const roleChanges = persistUserProjectAccess
                ? diffUserProjectRoles(projectsRef.current, mergedProjects, allProjectIds)
                : { add: [] as { projectId: string; role: string }[], remove: [] as { projectId: string; role: string }[] };

            const roleAddCount = roleChanges.add?.length ?? 0;
            const roleRemoveCount = roleChanges.remove?.length ?? 0;

            if (persistUserProjectAccess && (roleAddCount > 0 || roleRemoveCount > 0)) {
                try {
                    await persistUserProjectAccess({ roleChanges, syncedProjects: mergedProjects });
                } catch {
                    setBulkResultMessage('Could not save site permissions. Please try again.');
                    return;
                }
            }

            if (effectCount === 0 && (!persistUserProjectAccess || (roleAddCount === 0 && roleRemoveCount === 0))) {
                setBulkResultMessage(
                    bulkActionKind === BULK_ACTION_UNASSIGN
                        ? 'No changes: no sites had this role selected, or sites were not eligible to change.'
                        : 'No changes: sites already had this role, or were not eligible to change.'
                );
                return;
            }

            lastSyncedSig.current = JSON.stringify({ mergedProjects, disableSubmit });
            onFormChangeRef.current({ projects: mergedProjects, disableSubmit });

            setBulkResultMessage(resultParts.join(' '));
        } catch {
            setBulkResultMessage(
                typeof matrixTargetUserId === 'number' && persistUserProjectAccess
                    ? 'Could not apply bulk site permissions. Please try again.'
                    : 'Could not load sites. Please try again.'
            );
        } finally {
            setBulkApplying(false);
            bulkApplyLock.current = false;
        }
    }, [
        activeSearch,
        bulkActionKind,
        bulkRoleSelection,
        currentCustomerId,
        disabled,
        formMode,
        hasProjectLevelRole,
        isCurrentUserCustomerAdmin,
        isCurrentUserSuperAdmin,
        isInternalUser,
        persistUserProjectAccess,
        totalManageable,
        matrixRoleColumnNames,
        matrixTargetUserId,
        onSitePermissionsBulkSynced,
        roleStatsByProject,
        triggerBulkApplySiteRole,
        triggerProjectListAll,
    ]);

    if (!currentCustomerId) {
        return null;
    }

    const filterSitesSection = (
        <div className={styles.filterRow}>
            <div className={styles.filterLabel}>Filter sites in list</div>
            <Input id='matrix-filter-sites' placeholder='Type to filter…' value={inputValue} onChange={onInputChange} autoComplete='off' />
        </div>
    );

    const listResolved = !isProjectListFetching && projectListCurrent !== null;

    if (listResolved && totalManageable === 0) {
        return (
            <div className={styles.matrix}>
                <div className={styles.matrixToolbar}>{filterSitesSection}</div>
                <p className={styles.emptyMatrix} role='status'>
                    {activeSearch
                        ? 'No sites match your search, or you do not have permission to manage users on matching sites.'
                        : 'You do not have permission to manage users on any sites for this organisation, or no eligible sites exist yet.'}
                </p>
            </div>
        );
    }

    const showTableOverlay = isSavingChanges || bulkApplying || isProjectListFetching || matrixRolesFetching || userCountsFetching;

    /** Shown only for large tenants so occasional users are not overwhelmed. Create-user flow is local-only until save — bulk is edit/update only. */
    const showBulkActionBar =
        showBulkSiteRoleActions &&
        formMode === Access.UPDATE &&
        !disabled &&
        totalManageable > MATRIX_BULK_ACTION_MIN_TOTAL &&
        visibleRoleColumns.length > 0;

    const bulkActionToolbar = showBulkActionBar ? (
        <div className={styles.bulkActionSection} data-testid='matrix-bulk-action-toolbar'>
            <div className={styles.bulkActionHeadingRow}>
                <div className={styles.bulkActionHeading} id='matrix-bulk-action-heading'>
                    Bulk action
                </div>
                <QuestionButtonHelp
                    type='custom'
                    placement='top'
                    zIndex={1400}
                    maxWidth={320}
                    helpText={
                        <div className={styles.bulkActionTooltipBody}>
                            <p>
                                Assign a role to this user on every site you can manage that matches the current list filter, or remove that
                                role from all of those sites at once. This applies to all filtered sites, not only the sites on this page.
                            </p>
                            <p>Sites where the role cannot be changed are skipped.</p>
                        </div>
                    }
                />
            </div>
            <div className={styles.bulkApplyRow} role='group' aria-labelledby='matrix-bulk-action-heading'>
                <Select
                    testId='matrix-bulk-action-kind-select'
                    placeholder='Action'
                    selectedValue={bulkActionKind}
                    onChange={v => setBulkActionKind(v as BulkActionKind)}
                    options={[
                        { value: BULK_ACTION_ASSIGN, label: 'Assign role' },
                        { value: BULK_ACTION_UNASSIGN, label: 'Unassign role' },
                    ]}
                    width={160}
                    isDisabled={bulkApplying}
                />
                <Select
                    testId='matrix-bulk-role-select'
                    placeholder='Choose role'
                    selectedValue={bulkRoleSelection || undefined}
                    onChange={setBulkRoleSelection}
                    options={visibleRoleColumns.map(role => ({
                        value: role,
                        label: getRoleLabel(role),
                    }))}
                    width={200}
                    isDisabled={bulkApplying}
                />
                <SecondaryButton type='button' disabled={!bulkRoleSelection || bulkApplying} onClick={() => setBulkConfirmOpen(true)}>
                    {bulkActionKind === BULK_ACTION_UNASSIGN ? 'Unassign from all' : 'Assign to all'} {totalManageable} site
                    {totalManageable === 1 ? '' : 's'}
                </SecondaryButton>
            </div>
        </div>
    ) : null;

    const colCount = 1 + visibleRoleColumns.length;
    const showHeaderCheckbox = [Access.CREATE, Access.UPDATE].includes(formMode) && !disabled;

    /** Skeleton row labels while summaries load — avoids empty table flash */
    const placeholderSiteRows =
        rows.length === 0 && projectsData?.length
            ? projectsData.map(p => (
                  <tr key={p.projectId} className={styles.rowPlaceholder}>
                      <th scope='row' className={styles.tdSite} colSpan={colCount}>
                          {p.projectName}
                      </th>
                  </tr>
              ))
            : null;

    return (
        <div className={styles.matrix}>
            <div className={styles.matrixToolbar}>
                {filterSitesSection}
                {bulkActionToolbar}
            </div>
            {showBulkSiteRoleActions && formMode === Access.UPDATE && bulkResultMessage ? (
                <p className={styles.bulkResult} role='status'>
                    {bulkResultMessage}
                </p>
            ) : null}
            <ControlledConfirmDialog
                open={bulkConfirmOpen && showBulkSiteRoleActions && formMode === Access.UPDATE}
                onOpenChange={setBulkConfirmOpen}
                variant='confirm'
                title={
                    bulkActionKind === BULK_ACTION_UNASSIGN
                        ? 'Unassign role from all filtered sites?'
                        : 'Assign role to all filtered sites?'
                }
                description={
                    bulkActionKind === BULK_ACTION_UNASSIGN ? (
                        <>
                            This will turn off <strong>{getRoleLabel(bulkRoleSelection)}</strong> for this user on every site you can manage
                            that matches the current list filter ({totalManageable} site
                            {totalManageable === 1 ? '' : 's'}). Sites where that role cannot be changed will be skipped.
                        </>
                    ) : (
                        <>
                            This will turn on <strong>{getRoleLabel(bulkRoleSelection)}</strong> for every site you can manage that matches
                            the current list filter ({totalManageable} site
                            {totalManageable === 1 ? '' : 's'}).
                        </>
                    )
                }
                confirmLabel={bulkActionKind === BULK_ACTION_UNASSIGN ? 'Unassign from all' : 'Assign to all'}
                onConfirm={() => {
                    setBulkConfirmOpen(false);
                    void runBulkSiteRoleAction();
                }}
            />
            <>
                <div className={styles.tableScroll}>
                    <table className={styles.table} data-testid='matrix-permissions-table'>
                        <thead>
                            <tr>
                                <th scope='col' className={styles.thSite}>
                                    Site
                                </th>
                                {visibleRoleColumns.map(roleName => {
                                    const colState = columnSelectAllState(roleName);
                                    const eligible =
                                        rows.filter(row => {
                                            if (!row.assignable) {
                                                return false;
                                            }
                                            const e = row.countByRoles.find(c => c.name === roleName);
                                            if (!e) {
                                                return false;
                                            }
                                            const isPA = hasProjectLevelRole({ projectId: row.projectId, role: Role.PROJECT_ADMIN });
                                            if (
                                                !roleVisibleInMatrix(roleName, {
                                                    isInternalUser,
                                                    isCurrentUserCustomerAdmin,
                                                    isCurrentUserProjectAdmin: isPA,
                                                    isActorSuperAdmin: isCurrentUserSuperAdmin,
                                                })
                                            ) {
                                                return false;
                                            }
                                            return !isCellCheckboxDisabled(e, {
                                                disabled,
                                                formMode,
                                                isCurrentUserCustomerAdmin,
                                                isCurrentUserProjectAdmin: isPA,
                                            });
                                        }).length > 0;
                                    return (
                                        <th key={roleName} scope='col' className={styles.thRole}>
                                            <div className={styles.thRoleInner}>
                                                {showHeaderCheckbox && eligible && (
                                                    <ColumnSelectAllCheckbox
                                                        state={colState}
                                                        onToggle={() => onColumnHeaderToggle(roleName)}
                                                        ariaLabel={`Select all ${getRoleLabel(roleName)}`}
                                                        disabled={false}
                                                        className={styles.headerCheckbox}
                                                    />
                                                )}
                                                <span>{getRoleLabel(roleName)}</span>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {placeholderSiteRows}
                            {rows.map(row => {
                                const isCurrentUserProjectAdmin = hasProjectLevelRole({
                                    projectId: row.projectId,
                                    role: Role.PROJECT_ADMIN,
                                });
                                return (
                                    <tr key={row.projectId} className={classNames(!row.assignable && styles.rowReadOnly)}>
                                        <th scope='row' className={styles.tdSite}>
                                            {row.projectName}
                                            {!row.assignable && <span className={styles.readOnlyHint}> (view only)</span>}
                                        </th>
                                        {visibleRoleColumns.map(roleName => {
                                            const entry = row.countByRoles.find(c => c.name === roleName);
                                            if (
                                                !roleVisibleInMatrix(roleName, {
                                                    isInternalUser,
                                                    isCurrentUserCustomerAdmin,
                                                    isCurrentUserProjectAdmin,
                                                    isActorSuperAdmin: isCurrentUserSuperAdmin,
                                                })
                                            ) {
                                                return null;
                                            }
                                            if (!entry || isRoleUnavailableForProject(entry)) {
                                                return (
                                                    <td
                                                        key={roleName}
                                                        className={styles.tdCell}
                                                        data-testid={`matrix-cell-${row.projectId}-${roleName}`}
                                                        data-role-unavailable='true'
                                                    />
                                                );
                                            }
                                            const checkboxDisabled = !row.assignable
                                                ? true
                                                : isCellCheckboxDisabled(entry, {
                                                      disabled,
                                                      formMode,
                                                      isCurrentUserCustomerAdmin,
                                                      isCurrentUserProjectAdmin,
                                                  });
                                            const highlight = matrixCellHighlightClass(entry);
                                            return (
                                                <td
                                                    key={roleName}
                                                    className={classNames(
                                                        styles.tdCell,
                                                        highlight === 'error' && styles.tdCellerror,
                                                        highlight === 'warning' && styles.tdCellwarning
                                                    )}
                                                    data-testid={`matrix-cell-${row.projectId}-${roleName}`}
                                                >
                                                    <div className={styles.cellInner}>
                                                        <Checkbox
                                                            checked={entry.checked}
                                                            onChange={v => onToggleCell(row.projectId, roleName, v)}
                                                            disabled={checkboxDisabled}
                                                            checkboxClassName={styles.matrixCheckbox}
                                                        />
                                                        {entry.roleUserCount !== null && entry.roleUserCount !== undefined ? (
                                                            <span
                                                                className={styles.userCount}
                                                                title={
                                                                    entry.roleUserLimitMax !== null && entry.roleUserLimitMax !== undefined
                                                                        ? `Users with this role on this site: ${entry.roleUserCount} (limit ${entry.roleUserLimitMax})`
                                                                        : `Users with this role on this site: ${entry.roleUserCount}`
                                                                }
                                                            >
                                                                <Users className={styles.userCountIcon} aria-hidden />
                                                                {entry.roleUserLimitMax !== null && entry.roleUserLimitMax !== undefined
                                                                    ? `${entry.roleUserCount} / ${entry.roleUserLimitMax}`
                                                                    : entry.roleUserCount}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {showTableOverlay && (
                        <div
                            className={styles.loadingOverlay}
                            data-testid='matrix-table-loading-overlay'
                            aria-busy='true'
                            aria-label={isSavingChanges ? 'Saving changes' : 'Loading table'}
                        >
                            <div className={styles.overlayContent}>
                                <Loader2 className={styles.loadingSpinner} role='progressbar' />
                                {isSavingChanges ? <span className={styles.overlaySavingLabel}>Saving changes…</span> : null}
                            </div>
                        </div>
                    )}
                </div>
                <div className={styles.matrixPagination}>
                    <span className={styles.matrixPaginationSummary}>
                        Showing{' '}
                        {totalManageable === 0
                            ? '0'
                            : `${(apiPage - 1) * MATRIX_SITES_PAGE_SIZE + 1}–${Math.min(
                                  apiPage * MATRIX_SITES_PAGE_SIZE,
                                  totalManageable
                              )}`}{' '}
                        of {totalManageable} sites you can manage
                    </span>
                    {(() => {
                        const lastPage = Math.max(1, Math.ceil(totalManageable / MATRIX_SITES_PAGE_SIZE));
                        return lastPage > 1 ? (
                            <Pagination currentPageNumber={apiPage} lastPageNumber={lastPage} onChange={setApiPage} />
                        ) : null;
                    })()}
                </div>
            </>
        </div>
    );
};

export default ProjectPermissionsMatrixEditable;
